/**
 * "Continue with Google" and "Continue with Facebook": each kicks off the
 * OAuth redirect. On return, better-auth sets the session cookie and creates
 * a personal organization (the household) on first signup. When an AI
 * assistant's authorization sent the parent here, the OAuth provider client
 * signs this page's query into the request, so the authorization resumes
 * after sign-in (see lib/auth-client.ts). A sign-in refused at the provider
 * comes back with `?error=` (lib/sign-in-error.ts); inside Meta's apps,
 * Facebook leads (lib/in-app-browser.ts). The button this browser last signed
 * in with says so, from a cookie the server sets at each sign-in. Under the
 * buttons, a quiet link opens a password form, for the test accounts that
 * Babia makes for app directory reviewers.
 */
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useId, useState } from "react";
import { track } from "../lib/analytics.ts";
import {
  getLastUsedLoginMethod,
  isAssistantSignIn,
  signIn,
  useSession,
} from "../lib/auth-client.ts";
import { META_APP_NAMES, metaInAppBrowser, signInOrder } from "../lib/in-app-browser.ts";
import { alertReturnPath } from "../lib/push-state.ts";
import {
  cleanedReturnAddress,
  errorReturnPath,
  passwordSignInMessage,
  returnedSignInError,
  type SignInProvider,
  type SignInReason,
  signInErrorMessage,
  signInReason,
} from "../lib/sign-in-error.ts";
import { cn } from "../lib/utils.ts";
import { Button, buttonVariants } from "./ui/button.tsx";
import { Input, Label } from "./ui/input.tsx";
import { Sheet } from "./ui/sheet.tsx";

/**
 * Whether this build offers Facebook: Bun inlines PUBLIC_FACEBOOK_APP_ID, and
 * the server turns Facebook on with the same id (server/env.ts). An unset
 * `process.env.…` throws in a browser (no `process`), so it is off there.
 */
const FACEBOOK_ON = facebookBuilt();

function facebookBuilt(): boolean {
  try {
    return Boolean(process.env.PUBLIC_FACEBOOK_APP_ID);
  } catch {
    return false;
  }
}

type Placement = "button" | "masthead";

const LABELS: Record<SignInProvider, string> = {
  google: "Continue with Google",
  facebook: "Continue with Facebook",
};

/** The sign-in in flight, so a failure that comes back through `?error=` can name it. */
const STARTED_KEY = "babia.sign-in";

function rememberStart(provider: SignInProvider, placement: Placement) {
  try {
    sessionStorage.setItem(STARTED_KEY, JSON.stringify({ provider, placement }));
  } catch {
    // Storage off (a private window): the returned failure goes unnamed.
  }
}

function recallStart(): { provider?: unknown; placement?: unknown } {
  try {
    const raw = sessionStorage.getItem(STARTED_KEY);
    sessionStorage.removeItem(STARTED_KEY);
    return raw ? (JSON.parse(raw) as { provider?: unknown; placement?: unknown }) : {};
  } catch {
    return {};
  }
}

/**
 * undefined until read; then this page load's returned failure, or null
 * once it has been shown. Module state is per page load on purpose: a
 * sign-in only ever comes back through a full page load (the provider's
 * redirect), so a second failure starts fresh.
 */
let returned: { reason: SignInReason; message: string } | null | undefined;
let settled = false;

/** The failure this page came back with, until the page has shown it. */
function readReturnedError(): string | null {
  if (returned === undefined) {
    const code = returnedSignInError(window.location.search);
    const reason = code === null ? null : signInReason(code);
    returned = reason ? { reason, message: signInErrorMessage(reason) } : null;
  }
  return returned?.message ?? null;
}

/**
 * After the first render: record the returned failure, and take it off the
 * address so a reload or a shared link does not repeat it. The component
 * keeps the message in its state; later mounts (after moving around the
 * app) show nothing. Not during render, because the router listens to
 * history. `history.replaceState`, not the router's navigate: its search
 * parsing would rewrite an assistant's signed query.
 */
function settleReturnedError() {
  if (settled) return;
  settled = true;
  readReturnedError();
  const failure = returned;
  returned = null;
  const cleaned = cleanedReturnAddress(window.location);
  if (cleaned !== null) history.replaceState(history.state, "", cleaned);
  if (!failure) return;
  const started = recallStart();
  track("sign in failed", {
    placement: started.placement === "masthead" ? "masthead" : "button",
    assistant: isAssistantSignIn(window.location),
    provider:
      started.provider === "google" || started.provider === "facebook"
        ? started.provider
        : "unknown",
    network: false,
    reason: failure.reason,
  });
}

function useSocialSignIn(placement: Placement) {
  // The provider being redirected to; every button waits meanwhile.
  const [pending, setPending] = useState<SignInProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function start(provider: SignInProvider) {
    const retry = "Sign-in failed. Please try again.";
    // From the landing page, or an AI assistant's authorization (/login).
    const assistant = isAssistantSignIn(window.location);
    const context = { placement, assistant, provider };
    track("sign in started", context);
    rememberStart(provider, placement);
    setError(null);
    setPending(provider);
    try {
      // Success redirects to the provider and control leaves the page.
      // better-auth reports a server-side failure in the result rather than
      // throwing; a network failure throws. Either way, stay here and offer a
      // retry. A tapped alert's page comes back with its baby, whichever way
      // the sign-in ends (lib/push-state.ts).
      const { error: failure } = await signIn.social({
        provider,
        callbackURL: alertReturnPath(window.location),
        errorCallbackURL: errorReturnPath(window.location, assistant),
        // Facebook skips a permission the parent declined before (the email)
        // unless the request asks again.
        ...(provider === "facebook" ? { additionalParams: { auth_type: "rerequest" } } : {}),
      });
      if (!failure) return;
      // An assistant's expired link answers { error: "invalid_signature" }.
      const code = (failure as { error?: unknown }).error;
      const reason = signInReason(typeof code === "string" ? code : null);
      track("sign in failed", { ...context, network: false, reason });
      // Babia's own sentences only, never the server's or the browser's.
      setError(reason === "other" ? retry : signInErrorMessage(reason));
    } catch {
      track("sign in failed", { ...context, network: true, reason: "other" });
      setError("Babia couldn’t be reached. Check your connection and try again.");
    }
    setPending(null);
  }

  return { pending, error, start };
}

/**
 * The full-width sign-in buttons, with the in-app browser hint and any error
 * beneath. `showReturned` shows a failure this page came back with; only the
 * first sign-in on a page passes it. `password` adds the reviewers' password
 * link: on the sign-in screens, never on the landing page's sign-up buttons.
 * Signed in (the comparison page), it is a way back to the log instead.
 */
export function SignInButtons({
  className,
  placement = "button",
  showReturned = false,
  password = false,
}: {
  className?: string;
  placement?: Placement;
  showReturned?: boolean;
  password?: boolean;
}) {
  const { data: session } = useSession();
  const { pending, error, start } = useSocialSignIn(placement);
  const [returnedError, setReturnedError] = useState(() =>
    showReturned ? readReturnedError() : null,
  );
  useEffect(() => {
    if (showReturned) settleReturnedError();
  }, [showReturned]);
  if (session) return <OpenBabia className={className} />;
  const app = metaInAppBrowser(navigator.userAgent);
  const message = error ?? returnedError;
  // "google" or "facebook" after a sign-in in this browser (server/auth.ts).
  const lastUsed = getLastUsedLoginMethod();
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {signInOrder(FACEBOOK_ON, app).map((provider) => (
        <Button
          key={provider}
          variant="secondary"
          onClick={() => {
            setReturnedError(null);
            void start(provider);
          }}
          disabled={pending !== null}
          className="relative min-h-[52px] w-full gap-3 text-[17px]"
        >
          {provider === "google" ? <GoogleMark /> : <FacebookMark />}
          {pending === provider ? "Redirecting…" : LABELS[provider]}
          {lastUsed === provider ? <LastUsedTag /> : null}
        </Button>
      ))}
      {app ? (
        <p className="text-center text-[14px] opacity-70">
          Google sign-in doesn’t work inside {META_APP_NAMES[app]}. To use Google, open this page in
          your browser from the ⋯ menu.
        </p>
      ) : null}
      {message ? (
        <p role="alert" className="text-center text-[14px] text-accent-2">
          {message}
        </p>
      ) : null}
      {password ? <PasswordSignIn placement={placement} /> : null}
    </div>
  );
}

/**
 * "Sign in with a password", a quiet link until tapped. Only the test
 * accounts that Babia makes for app directory reviewers have a password
 * (server/auth.ts), because the directories refuse sign-in codes. A browser
 * that signed in this way before opens the form at once. An assistant's
 * authorization resumes after it as after Google: the OAuth provider client
 * signs this page's query into the request (lib/auth-client.ts), and the
 * answer redirects. The inputs are uncontrolled, so React never copies a
 * password into a `value` attribute, and `ph-no-capture` keeps the form
 * out of session replays and autocapture.
 */
function PasswordSignIn({ placement }: { placement: Placement }) {
  const id = useId();
  const [open, setOpen] = useState(() => getLastUsedLoginMethod() === "email");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <Button variant="ghost" onClick={() => setOpen(true)} className="self-center opacity-65">
        Sign in with a password
      </Button>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const assistant = isAssistantSignIn(window.location);
    const context = { placement, assistant, provider: "password" };
    track("sign in started", context);
    setError(null);
    setPending(true);
    try {
      // Success redirects (better-auth's redirect fetch plugin), and control
      // leaves the page; after an assistant's sign-in, to its consent screen.
      // The button stays disabled meanwhile. A success without a redirect
      // must not leave the form stuck, so it is usable again.
      const { data, error: failure } = await signIn.email({
        email: String(form.get("email") ?? "").trim(),
        password: String(form.get("password") ?? ""),
        callbackURL: alertReturnPath(window.location),
      });
      if (!failure) {
        if ((data as { redirect?: unknown } | null)?.redirect !== true) setPending(false);
        return;
      }
      // An assistant's expired link answers { error: "invalid_signature" }.
      const code = (failure as { error?: unknown }).error;
      const known = typeof code === "string" ? code : null;
      track("sign in failed", { ...context, network: false, reason: signInReason(known) });
      setError(passwordSignInMessage(failure.status, known));
    } catch {
      track("sign in failed", { ...context, network: true, reason: "other" });
      setError("Babia couldn’t be reached. Check your connection and try again.");
    }
    setPending(false);
  }

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="ph-no-capture mt-2 flex flex-col gap-3 border-t border-divider pt-4"
    >
      <p className="text-[14px] opacity-65">
        Passwords are only for test accounts that Babia set up. Parents sign in with Google or
        Facebook.
      </p>
      <div>
        <Label htmlFor={`${id}-email`}>Email</Label>
        <Input
          id={`${id}-email`}
          name="email"
          type="email"
          className="mt-1"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
        />
      </div>
      <div>
        <Label htmlFor={`${id}-password`}>Password</Label>
        <Input
          id={`${id}-password`}
          name="password"
          type="password"
          className="mt-1"
          autoComplete="current-password"
          required
        />
      </div>
      <Button type="submit" variant="secondary" disabled={pending} className="min-h-[52px] w-full">
        {pending ? "Signing in…" : "Sign in"}
      </Button>
      {error ? (
        <p role="alert" className="text-center text-[14px] text-accent-2">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/** A quiet "Sign in" for the masthead, for parents who already have a log. */
export function SignInLink({ className }: { className?: string }) {
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  if (session) {
    return (
      <Link to="/" className={cn(buttonVariants({ variant: "ghost" }), "text-[15px]", className)}>
        Open Babia
      </Link>
    );
  }
  return (
    <>
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        className={cn("text-[15px]", className)}
      >
        Sign in
      </Button>
      <Sheet open={open} title="Sign in" onClose={close}>
        <p className="mt-2 text-[14px] opacity-65">Use the account you signed up with.</p>
        <SignInButtons className="mt-5" placement="masthead" password />
      </Sheet>
    </>
  );
}

/** For a parent who is already signed in: back to the log. */
function OpenBabia({ className }: { className?: string }) {
  return (
    <div className={className}>
      <Link
        to="/"
        className={cn(buttonVariants({ variant: "secondary" }), "min-h-[52px] w-full text-[17px]")}
      >
        Open Babia
      </Link>
    </div>
  );
}

/**
 * "Last used", across the top edge of the button, clear of the label on a
 * narrow phone. Screen readers hear it after the label.
 */
function LastUsedTag() {
  return (
    <span className="absolute -top-2 right-3 rounded-sm bg-accent-tint px-1.5 text-[11px] leading-4 font-normal tracking-[0.09em] text-accent-deep uppercase">
      <span className="sr-only">, </span>
      Last used
    </span>
  );
}

/** Google "G" mark (inline SVG so there's no asset dependency). */
function GoogleMark() {
  return (
    <svg className="size-4" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * Facebook's round "f" mark (Simple Icons, CC0), in Facebook blue with a
 * white "f" on every paper colour (inline SVG so there's no asset dependency).
 */
function FacebookMark() {
  return (
    <svg className="size-4" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11.5" fill="#fff" />
      <path
        fill="#1877F2"
        d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z"
      />
    </svg>
  );
}
